import { format } from 'date-fns';
import { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertBody, AlertDescription, AlertIcon, Button, Flex, InformationCircleOutline, Text, Tooltip, VStack } from '@bonusly/design-system';
import { EventFrequency } from '@bonusly/one-on-ones-entities';

import { DateInput } from './date-input';
import { FrequencyInput } from './frequency-input';
import { OccurrenceInput } from './occurrence-input';
import { TimeInput } from './time-input';

interface EventFormProps {
	isNewMeeting: boolean;
	openLinkCalendar?: () => void;
}

// NOTE: The form itself is shared between the create and edit modals and behaves exactly the same way in both.
// So, everything here is a shared component. The individual create and edit modals are responsible for populating
// the form with the initial data, either default values when creating a new event, or the existing event data when
// editing an existing event. Note that there are no conditionals here checking if the Event record is being edited
// or not. That is all determined by the modal rendering this component.

export const EventForm: FC<EventFormProps> = ({ isNewMeeting, openLinkCalendar }) => {
	const { t } = useTranslation('one_on_ones');
	const frequency = useWatch({ name: 'frequency' });
	const dayOfWeek = useWatch({ name: 'dayOfWeek' });
	const occurrenceInMonth = useWatch({ name: 'occurrenceInMonth' });
	const startTime = useWatch({ name: 'startTime' });
	const {
		formState: { errors: formErrors },
	} = useFormContext();

	// every week, every other week, monthly
	const recurrenceFrequency = t(`meeting.modal.form.recurrence_frequency.${frequency}`);

	// Monday, Tuesday, Wednesday, etc.
	const selectedDayName = t(`schedule_day_of_week_options.${dayOfWeek}`);

	const recurrenceDayLabel =
		frequency === EventFrequency.Monthly
			? t(`meeting.modal.form.cadence.monthly_${occurrenceInMonth}_partial`, {
					// the second {day}, the fourth {day}, the last {day}
					day: selectedDayName,
				})
			: selectedDayName;

	// Repeats monthly on the second Monday, Repeats weekly on Monday, etc
	const recurrenceInfoLabel =
		t('meeting.modal.form.recurrence_info_label', {
			frequency: recurrenceFrequency,
			recurrenceDay: recurrenceDayLabel,
		}) +
		(startTime
			? ' ' +
				t('meeting.modal.form.recurrence_time_label', {
					// at 9:00 AM, at 3:00 PM, etc
					time: format(startTime, 'h:mm a'),
				})
			: '');

	// Only show the recurrence info label if the frequency is not monthly or
	// if the frequency is monthly and an occurrence in month is selected
	const showRecurrenceInfoLabel = frequency !== EventFrequency.Monthly || !!(frequency === EventFrequency.Monthly && occurrenceInMonth);

	return (
		<Flex direction="column" gap={4}>
			<Flex flexDirection="column" gap={2}>
				<Flex flexDirection="row" justify="space-between" align="center">
					<Text as="label" color="text.softer" fontSize={100} fontWeight="medium">
						<>{isNewMeeting ? t('meeting.modal.form.new_meeting_time_label') : t('meeting.modal.form.edit_meeting_time_label')}</>
					</Text>
					<Flex alignItems="flex-end" gap={1}>
						<Button fontSize="sm" onClick={openLinkCalendar} variant="link">
							{t('meeting.modal.link_calendar')}
						</Button>

						<Tooltip label={'Sync your calendar to easily schedule and manage your 1:1s.'} onOpen={() => {}} placement="top" alignSelf="center">
							<Flex textAlign="right" alignItems="center">
								<InformationCircleOutline color="gray.500" size="sm" alignSelf="center" _hover={{ color: 'gray.900' }} />
							</Flex>
						</Tooltip>
					</Flex>
				</Flex>

				<DateInput />
				<TimeInput />

				{(formErrors.startTime || formErrors.endTime) && (
					<Alert status="error" size="sm">
						<AlertIcon />

						<AlertBody>
							<AlertDescription>{formErrors.startTime ? String(formErrors.startTime.message) : String(formErrors.endTime?.message ?? '')}</AlertDescription>
						</AlertBody>
					</Alert>
				)}
			</Flex>

			<VStack width="full" spacing={2} align="flex-start">
				<Text as="label" color="text.softer" fontSize={100} fontWeight="medium">
					{isNewMeeting ? t('meeting.modal.form.cadence.new_meeting_frequency') : t('meeting.modal.form.cadence.edit_meeting_frequency')}
				</Text>
				<Flex flexDirection={{ base: 'column', md: 'row' }} maxWidth="full" gap={{ base: 2, md: 4 }} alignItems="flex-start" width="full">
					<FrequencyInput />
					{frequency === EventFrequency.Monthly && <OccurrenceInput />}
				</Flex>
				{formErrors.occurrenceInMonth && (
					<Alert status="error" size="sm">
						<AlertIcon />
						<AlertBody>
							<AlertDescription>{String(formErrors.occurrenceInMonth.message)}</AlertDescription>
						</AlertBody>
					</Alert>
				)}
				{showRecurrenceInfoLabel && (
					<Text color="text.softest" fontSize={50} fontWeight="normal">
						{recurrenceInfoLabel}
					</Text>
				)}
			</VStack>
		</Flex>
	);
};
